import { useState, useEffect } from 'react'
import { createPortal } from 'react-dom'
import { Check, Shield } from 'lucide-react'
import { useI18n } from '../i18n'
import InsuranceCandidateList from './InsuranceCandidateList'

/**
 * InsuranceCandidateSheet — HomeScreen host for the discovery pick-list.
 *
 * D (v242.7.0): the DiscoveryToast nudge escalates into this bottom sheet.
 * Wraps the shared InsuranceCandidateList (variant='sheet') and adds the
 * sheet chrome (scrim, grabber, header) + the done-confirmation stage.
 *
 * Persistence stays in the host's store actions, passed through:
 *   onApplyPrimary   → applyDiscoveryResult(idx)
 *   onApplySecondary → applyDiscoverySecondary(idx, userId)
 *   onDismiss        → dismissDiscoveryResult()   (close without applying)
 *   onManual         → route to Profile → Insurance form
 *
 * Props:
 *   open            — boolean
 *   discoveryResult — profiles.insurance.discovery_result
 *   onApplyPrimary / onApplySecondary — async (idx) => { success }
 *   onDismiss       — () => void   (X / scrim / grabber pull-down)
 *   onManual        — () => void
 *   onClose         — () => void   (fully close after done)
 */

const NAVY = '#15354F'
const BLUE = '#46A8F9'
const GREEN = '#66D170'
const GREEN_INK = '#2F9E4A'
const SLATE_INK = '#64748B'
const LIGHT = '#A7D9FC'
const ICE = '#E9F8FF'

export default function InsuranceCandidateSheet({
  open,
  discoveryResult,
  onApplyPrimary,
  onApplySecondary,
  onDismiss,
  onManual,
  onClose,
}) {
  const { t } = useI18n()
  const [mounted, setMounted] = useState(false)
  const [visible, setVisible] = useState(false)
  const [stage, setStage] = useState('list')      // 'list' | 'done'
  const [didSecondary, setDidSecondary] = useState(false)

  useEffect(() => {
    if (open) {
      setMounted(true)
      setStage('list')
      setDidSecondary(false)
      const t = setTimeout(() => setVisible(true), 20)
      return () => clearTimeout(t)
    }
    setVisible(false)
    const t = setTimeout(() => setMounted(false), 320)
    return () => clearTimeout(t)
  }, [open])

  if (!mounted) return null

  const candidates = (discoveryResult && discoveryResult.candidates) || []
  const singleConfirm = discoveryResult && discoveryResult.status === 'found' && candidates.length === 1
  // PROD-424 (f), patient push 3a: every sentence on this sheet goes through t() (en + es)
  const appliedPayer = (candidates.length && candidates[0].payerName) || t('insuranceCandidates.yourPlan')

  const close = () => { setVisible(false); setTimeout(() => { onClose?.() }, 300) }
  const dismiss = () => { setVisible(false); setTimeout(() => { onDismiss?.() }, 300) }

  return createPortal((
    <div className="fixed inset-0 z-[60]" aria-hidden={!open}>
      {/* scrim */}
      <div
        onClick={dismiss}
        style={{ position: 'absolute', inset: 0, background: 'rgba(21,53,79,0.34)',
                 opacity: visible ? 1 : 0, transition: 'opacity 0.28s' }}
      />
      {/* sheet */}
      <div
        style={{
          position: 'absolute', left: 0, right: 0, bottom: 0, background: '#fff',
          borderRadius: '26px 26px 0 0', boxShadow: '0 -10px 40px rgba(21,53,79,0.22)',
          transform: visible ? 'translateY(0)' : 'translateY(100%)',
          transition: 'transform 0.32s cubic-bezier(0.2,0.8,0.2,1)',
          maxHeight: '90%', display: 'flex', flexDirection: 'column',
          paddingBottom: 'env(safe-area-inset-bottom)',
        }}>
        <div onClick={dismiss}
             style={{ width: 38, height: 5, borderRadius: 999, background: '#D8E2EC', margin: '9px auto 2px', cursor: 'pointer' }} />

        {stage === 'list' && (
          <>
            <div style={{ padding: '4px 18px 10px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div style={{ fontSize: 16, fontWeight: 800, color: NAVY }}>{t('insuranceCandidates.confirmYourInsurance', 'Confirm your insurance')}</div>
              <button onClick={dismiss}
                      style={{ width: 30, height: 30, borderRadius: 9, background: '#F1F5F9', border: 0,
                               display: 'flex', alignItems: 'center', justifyContent: 'center', color: NAVY, cursor: 'pointer' }}>✕</button>
            </div>
            <div style={{ overflowY: 'auto', padding: '0 18px 18px' }}>
              <div style={{ margin: '2px 2px 14px' }}>
                <div style={{ fontSize: 18, fontWeight: 800, letterSpacing: '-0.01em', color: NAVY }}>
                  {singleConfirm ? t('insuranceCandidates.foundYourPlan') : t('insuranceCandidates.foundPossiblePlans')}
                </div>
                <div style={{ fontSize: 12.5, color: SLATE_INK, marginTop: 4, lineHeight: 1.5 }}>
                  {singleConfirm
                    ? t('insuranceCandidates.confirmCoverageBody')
                    : t('insuranceCandidates.multipleMatchesBody')}
                </div>
              </div>
              <InsuranceCandidateList
                candidates={candidates}
                variant="sheet"
                singleConfirm={singleConfirm}
                onApplyPrimary={onApplyPrimary}
                onApplySecondary={onApplySecondary}
                onSkip={onManual}
                onDone={(secondary) => { setDidSecondary(!!secondary); setStage('done') }}
              />
            </div>
          </>
        )}

        {stage === 'done' && (
          <div style={{ padding: '30px 24px 24px' /* PROD-1176 (A6): the sheet panel pays the bottom inset */, textAlign: 'center' }}>
            <div style={{ width: 66, height: 66, borderRadius: '50%', background: 'rgba(102,209,112,0.14)',
                          border: `2px solid ${GREEN}`, display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px' }}>
              <Check size={30} strokeWidth={2.6} style={{ color: GREEN_INK }} />
            </div>
            <div style={{ fontSize: 19, fontWeight: 800, color: NAVY }}>{t('insuranceCandidates.coverageAdded', 'Coverage added')}</div>
            <div style={{ fontSize: 13, color: SLATE_INK, marginTop: 6, lineHeight: 1.5 }}>
              {t('insuranceCandidates.nowOnProfile', { payer: appliedPayer })}
            </div>
            {didSecondary && (
              <div style={{ background: ICE, borderRadius: 14, padding: 13, marginTop: 18, textAlign: 'left',
                            display: 'flex', alignItems: 'center', gap: 11 }}>
                <Shield size={18} style={{ color: BLUE, flexShrink: 0 }} />
                <div>
                  <div style={{ fontSize: 12, fontWeight: 800, color: NAVY }}>{t('insuranceCandidates.secondaryAdded', 'Secondary added')}</div>
                  <div style={{ fontSize: 11, color: SLATE_INK }}>{t('insuranceCandidates.billedSecond', 'Billed second after your primary plan')}</div>
                </div>
              </div>
            )}
            <button
              onClick={close}
              style={{ width: '100%', padding: 14, borderRadius: 999, border: 0, background: GREEN, color: '#fff',
                       fontSize: 14, fontWeight: 800, cursor: 'pointer', marginTop: 20 }}>
              {t('common.done', 'Done')}
            </button>
          </div>
        )}
      </div>
    </div>
  ), document.body)
}
